import { useContext, useRef, useCallback } from 'react';
import { CoreContext } from '../context/index.js';
import logger from '../utils/logger.js';
import 'cookies-next';
import '../currency-_kacFJHO.js';
import '../language-BHHwgtQp.js';
import '../payment-5CMab_Gs.js';
import '../utils/currency.js';
import 'lodash/get';
import '../utils/price.js';
import '../product-_pRduMz7.js';
import 'next/server';
import '../index-Oip0gjfj.js';
import 'next/router';
import '../helpers-qyZpLI0y.js';
import 'lodash';
import '../index-QrdBfapu.js';
import { i as isConsentDetermined, g as getKwkConsent, t as trackHomepage, a as trackProduct, b as trackListing, c as trackBasket, d as trackTransaction, e as trackLeadInscription, f as trackLeadConfirmation } from '../kwkUni-2OQfB-dG.js';
import useConsentModeListener from './useConsentModeListener.js';
import '../utils/logToSlack.js';
import 'number-currency-format';
import '../utils/product.js';
import '../paths-ZKN3m62_.js';
import '../cookies-l-jEMp6P.js';
import '../utils/cookies.js';
import '../utils/isBot.js';

/**
 * Hook for KWK tracking that waits for consent to be determined
 * Uses the existing consent infrastructure to ensure proper consent handling
 */
const useKwankoTracking = () => {
    const { data: { siteConfig: { parentHotelCompany, accorAffiliateRetargetingEnabled: kwkEnabled = false }, }, } = useContext(CoreContext);
    const consentDeterminedRef = useRef(false);
    const pendingTrackingRef = useRef([]);
    const firedWhileDeniedRef = useRef([]);
    const lastConsentRef = useRef(undefined);
    // Check if we should track KWK
    const shouldTrack = kwkEnabled && parentHotelCompany === 'Accor';
    // Function to execute pending tracking calls
    const executePendingTracking = async () => {
        if (pendingTrackingRef.current.length > 0) {
            logger('[KWK] Executing pending tracking calls after consent determined', {
                category: 'analytics',
                type: 'log',
            });
            const calls = [...pendingTrackingRef.current];
            pendingTrackingRef.current = [];
            // Check current consent status
            const currentConsent = getKwkConsent();
            // Execute pending calls concurrently with error isolation
            const results = await Promise.allSettled(calls.map(trackingCall => {
                // If consent is denied, queue this call for potential re-triggering
                if (currentConsent === '0') {
                    firedWhileDeniedRef.current.push(trackingCall);
                }
                return trackingCall();
            }));
            results.forEach(result => {
                if (result.status === 'rejected') {
                    logger(`[KWK] Error executing pending tracking call: ${result.reason}`, {
                        category: 'analytics',
                        type: 'error',
                    });
                }
            });
        }
    };
    // Function to queue tracking calls if consent not yet determined
    const queueOrExecuteTracking = useCallback(async (trackingCall) => {
        if (!shouldTrack) {
            return;
        }
        if (isConsentDetermined()) {
            // Consent is determined, execute immediately
            consentDeterminedRef.current = true;
            const currentConsent = getKwkConsent();
            // If consent is currently denied, remember this call to replay on grant
            if (currentConsent === '0') {
                firedWhileDeniedRef.current.push(trackingCall);
            }
            lastConsentRef.current = currentConsent;
            await trackingCall();
        }
        else {
            // Consent not yet determined, queue the call
            pendingTrackingRef.current.push(trackingCall);
            logger('[KWK] Consent not yet determined, queuing tracking call', {
                category: 'analytics',
                type: 'log',
            });
        }
    }, [shouldTrack]);
    // Listen for consent changes using the shared consent listener (event/observer based)
    useConsentModeListener(useCallback(() => {
        if (!shouldTrack)
            return;
        if (isConsentDetermined()) {
            const currentConsent = getKwkConsent();
            if (!consentDeterminedRef.current) {
                consentDeterminedRef.current = true;
                lastConsentRef.current = currentConsent;
                executePendingTracking().catch(error => {
                    logger(`[KWK] Error executing pending tracking calls: ${error}`, {
                        category: 'analytics',
                        type: 'error',
                    });
                });
            }
            // If consent just flipped to granted, replay calls fired under denial
            if (lastConsentRef.current === '0' &&
                currentConsent === '1' &&
                firedWhileDeniedRef.current.length > 0) {
                const callsToReplay = [...firedWhileDeniedRef.current];
                firedWhileDeniedRef.current = [];
                lastConsentRef.current = '1';
                Promise.allSettled(callsToReplay.map(call => call()))
                    .then(results => {
                    results.forEach(result => {
                        if (result.status === 'rejected') {
                            logger(`[KWK] Error re-sending tracking call after consent grant: ${result.reason}`, {
                                category: 'analytics',
                                type: 'error',
                            });
                        }
                    });
                })
                    .catch(error => {
                    // This catch is for any synchronous errors in the Promise.allSettled chain itself
                    // (allSettled never rejects, but the .then() could throw)
                    logger(`[KWK] Unexpected error in replay chain: ${error}`, {
                        category: 'analytics',
                        type: 'error',
                    });
                });
            }
            else {
                lastConsentRef.current = currentConsent;
            }
        }
    }, [shouldTrack]));
    // Return tracking functions that respect consent
    const trackHomepageCb = useCallback(() => {
        queueOrExecuteTracking(() => trackHomepage());
    }, [queueOrExecuteTracking]);
    const trackProductCb = useCallback((params) => {
        queueOrExecuteTracking(() => trackProduct(params));
    }, [queueOrExecuteTracking]);
    const trackListingCb = useCallback((params) => {
        queueOrExecuteTracking(() => trackListing(params));
    }, [queueOrExecuteTracking]);
    const trackBasketCb = useCallback((params) => {
        queueOrExecuteTracking(() => trackBasket(params));
    }, [queueOrExecuteTracking]);
    const trackTransactionCb = useCallback((params) => {
        queueOrExecuteTracking(() => trackTransaction(params));
    }, [queueOrExecuteTracking]);
    const trackLeadInscriptionCb = useCallback(() => {
        queueOrExecuteTracking(() => trackLeadInscription());
    }, [queueOrExecuteTracking]);
    const trackLeadConfirmationCb = useCallback((params) => {
        queueOrExecuteTracking(() => trackLeadConfirmation(params));
    }, [queueOrExecuteTracking]);
    return {
        trackHomepage: trackHomepageCb,
        trackProduct: trackProductCb,
        trackListing: trackListingCb,
        trackBasket: trackBasketCb,
        trackTransaction: trackTransactionCb,
        trackLeadInscription: trackLeadInscriptionCb,
        trackLeadConfirmation: trackLeadConfirmationCb,
    };
};

export { useKwankoTracking };
//# sourceMappingURL=useKwankoTracking.js.map
